<template>
  <div>
    <a-row
      :gutter="[16, 16]"
      :style="{ marginBottom: '1rem' }">
      <a-col :xs="24">
        <a-button
          type="primary"
          :style="{ width: '100%' }"
          @click="openCreateTagModal">
          Создать тег
        </a-button>
      </a-col>
    </a-row>

    <a-row
      :style="{ marginBottom: '1rem' }"
      :gutter="[16, 16]"
      :wrap="true">
      <a-col
        :xs="24"
        :md="12">
        <a-auto-complete
          v-model:value="filters.nameQuery"
          :options="tagNameSuggestions"
          :style="{ width: '100%' }"
          placeholder="Поиск по названию (RU/EN)" />
      </a-col>
      <a-col
        :xs="24"
        :md="12">
        <a-auto-complete
          v-model:value="filters.categoryQuery"
          :options="tagCategorySuggestions"
          :style="{ width: '100%' }"
          placeholder="Поиск по категории (RU/EN)" />
      </a-col>
    </a-row>

    <a-row :style="{ marginBottom: '1rem' }">
      <a-col :xs="24">
        <a-button
          :style="{ width: '100%' }"
          @click="resetFilters">
          Сбросить фильтры
        </a-button>
      </a-col>
    </a-row>

    <a-table
      :data-source="filteredTags"
      :pagination="false"
      row-key="id">
      <a-table-column title="Название">
        <template #default="{ record }">
          {{ getTagLabel(record) }}
        </template>
      </a-table-column>
      <a-table-column title="Категория">
        <template #default="{ record }">
          {{ getTagCategoryLabel(record) }}
        </template>
      </a-table-column>
      <a-table-column title="Действия">
        <template #default="{ record }">
          <a-space>
            <a-button
              type="link"
              @click="openEditTagModal(record)">
              Редактировать
            </a-button>
            <a-popconfirm
              title="Удалить тег?"
              ok-text="Удалить"
              cancel-text="Отмена"
              @confirm="removeTag(record.id)">
              <a-button type="link">
                Удалить
              </a-button>
            </a-popconfirm>
          </a-space>
        </template>
      </a-table-column>
    </a-table>

    <a-modal
      v-model:open="isModalOpen"
      :title="editingTagId ? 'Редактировать тег' : 'Создать тег'"
      :ok-text="editingTagId ? 'Сохранить' : 'Создать'"
      cancel-text="Отмена"
      @ok="submit">
      <a-form layout="vertical">
        <a-form-item label="Категория RU">
          <a-auto-complete
            v-model:value="form.ru_category"
            :options="ruCategoryOptions"
            :style="{ width: '100%' }"
            placeholder="Например: вид проёма" />
        </a-form-item>
        <a-form-item label="Категория EN">
          <a-auto-complete
            v-model:value="form.en_category"
            :options="enCategoryOptions"
            :style="{ width: '100%' }"
            placeholder="Например: aperture type" />
        </a-form-item>
        <a-form-item label="Название RU">
          <a-input v-model:value="form.ru" />
        </a-form-item>
        <a-form-item label="Название EN">
          <a-input v-model:value="form.en" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { message } from 'ant-design-vue';
import { showApiError } from '@/helpers/apiError';
import { getTagCategoryLabel, getTagLabel, matchesSearchTokens } from '@/helpers/tagLabels';

const props = defineProps({
  /** Неймспейс vuex-модуля сущности: должен содержать действия createTag/updateTag/deleteTag. */
  storeNamespace: { type: String, required: true },
  tags: { type: Array, default: () => [] },
});

const store = useStore();

const filters = reactive({ nameQuery: '', categoryQuery: '' });

const buildOptions = (getValues) => {
  const values = new Set();

  props.tags.forEach((tag) => getValues(tag).forEach((value) => value && values.add(value)));

  return Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .map((value) => ({ value }));
};

const tagNameSuggestions = computed(() => buildOptions((tag) => [tag.ru, tag.en]));
const tagCategorySuggestions = computed(() => buildOptions((tag) => [tag.ru_category, tag.en_category]));
const ruCategoryOptions = computed(() => buildOptions((tag) => [tag.ru_category]));
const enCategoryOptions = computed(() => buildOptions((tag) => [tag.en_category]));

const filteredTags = computed(() => props.tags.filter((tag) => (
  matchesSearchTokens(filters.nameQuery, [tag.ru, tag.en])
  && matchesSearchTokens(filters.categoryQuery, [tag.ru_category, tag.en_category])
)));

const resetFilters = () => {
  filters.nameQuery = '';
  filters.categoryQuery = '';
};

const isModalOpen = ref(false);
const editingTagId = ref(null);
const form = reactive({
  ru_category: '', en_category: '', ru: '', en: '',
});

const fillForm = (tag) => {
  form.ru_category = tag?.ru_category || '';
  form.en_category = tag?.en_category || '';
  form.ru = tag?.ru || '';
  form.en = tag?.en || '';
};

const openCreateTagModal = () => {
  editingTagId.value = null;
  fillForm(null);
  isModalOpen.value = true;
};

const openEditTagModal = (tag) => {
  editingTagId.value = tag.id;
  fillForm(tag);
  isModalOpen.value = true;
};

const submit = async () => {
  const payload = {
    ru_category: form.ru_category.trim(),
    en_category: form.en_category.trim(),
    ru: form.ru.trim(),
    en: form.en.trim(),
  };

  if (Object.values(payload).some((value) => !value)) {
    message.error('Заполните категорию и название на RU и EN');
    return;
  }

  const isEditing = Boolean(editingTagId.value);

  try {
    if (isEditing) {
      await store.dispatch(`${props.storeNamespace}/updateTag`, { id: editingTagId.value, payload });
    } else {
      await store.dispatch(`${props.storeNamespace}/createTag`, payload);
    }

    isModalOpen.value = false;
    editingTagId.value = null;
    message.success(isEditing ? 'Тег обновлен' : 'Тег создан');
  } catch (error) {
    showApiError(error, isEditing ? 'Не удалось обновить тег' : 'Не удалось создать тег');
  }
};

const removeTag = async (id) => {
  try {
    await store.dispatch(`${props.storeNamespace}/deleteTag`, id);
    message.success('Тег удален');
  } catch (error) {
    showApiError(error, 'Не удалось удалить тег');
  }
};
</script>
